/**
 * Arbeitsplan am Projekt — Antwort von `GET /api/kontor/projekt/{id}/arbeitsplan`
 * (hetzner/webhook/kontor_arbeitsplan.py, Plan Projekt-Tracking Etappe 5/6).
 *
 * Ein Arbeitstag ist ein Termin ohne Datum (`planung_typ = 'vorplanung'`). Beim
 * Eintragen in den Kalender wird dieselbe Zeile zum Termin — Leistungen,
 * „Wer führt aus" und „Was muss da sein" sind dann schon dran.
 */

export type PlanPosition = {
  /** Positionsanker `<Belegnummer>#<ID oder idxN>`. */
  id: string;
  titel: string;
  menge: number;
  einheit: string;
  gesamt_netto: number;
  /** An welchen Tagen (Tag-Nummern) die Leistung steht. */
  tage: number[];
  packzettel: { id: string; nummer: string } | null;
  /** Hängt übernommenes Material an der Position — am Tag wird es als Lieferung/Maschine vorgeschlagen. */
  material: boolean;
  materialien: { id: string; name: string; art: string }[];
};

/** Vorschlag aus dem Angebot (Etappe 6b): Material einer Position, die an diesem Tag steht. */
export type PlanVorschlag = {
  projekt_material_id: string;
  kategorie: "lieferung" | "maschine";
  titel: string;
  lieferant_id: string;
  /** Maschine: letzter Tag der Position. */
  bis_termin_id: string;
  bis_plan_nr: number | null;
  anker: string;
  /** Titel der Angebotsposition, aus der der Vorschlag kommt. */
  position: string;
};

export type BedarfArt = "lieferung" | "maschine" | "besorgen" | "hinweis";

export type PlanBedarf = {
  id: string;
  termin_id: string;
  kategorie: BedarfArt | "werkzeug" | string;
  titel: string;
  details: string;
  /** „07:00" oder "" — bei Lieferung/Maschine die Anlieferzeit. */
  uhrzeit: string | null;
  erledigt: boolean;
  lieferant_id: string;
  lieferant_name: string;
  projekt_material_id: string;
  material_name: string;
  bestell_status: string;
  /** Maschine über mehrere Tage: der letzte Miettag. */
  bis_termin_id: string;
  bis_plan_nr: number | null;
};

export type PlanTag = {
  id: string;
  plan_nr: number | null;
  titel: string;
  notiz: string;
  /** Kalendertag in Berlin, "" ohne Datum. */
  datum: string;
  start_at: string;
  end_at: string;
  im_kalender: boolean;
  nacharbeit: boolean;
  packzettel_id: string;
  /** „Eigenes Team" oder „Nico · PZ0003". */
  wer: string;
  /** Etappe 7: der Tag liegt in der Vergangenheit. */
  vorbei: boolean;
  /** Die Leistungen des Tages = seine Checkliste (Team-App und Kontor, mit Namen). */
  positionen: {
    anker: string; titel: string; bekannt: boolean;
    erledigt_am: string; erledigt_von: string;
    /** an einem anderen Tag schon abgehakt */
    anderswo_erledigt: boolean;
  }[];
  bedarf: PlanBedarf[];
  besetzung: { person_id: string; person_typ: string; name: string; status: string }[];
  vorschlaege: PlanVorschlag[];
};

export type PlanPackzettel = {
  id: string;
  nummer: string;
  empfaenger: string;
  betreff: string;
  helfer_id: string;
  helfer_name: string;
  pauschale_id: string;
  pauschale_bezeichnung: string;
  pauschale_betrag: number;
  beleg_nummer: string;
  positionen: string[];
  budget_netto: number;
};

export type PlanMaterial = {
  id: string;
  name: string;
  art: string;
  /** An welchen Tagen das Material als Bedarf steht (Liefertag). */
  tage: { tag_id: string; plan_nr: number | null; datum: string }[];
  menge: string;
  einheit: string;
  bestell_status: string;
  lieferant_id: string;
};

export type Arbeitsplan = {
  projekt_id: string;
  titel: string;
  angebot: { id: string; nummer: string; status: string; datum: string; netto: number; positionen: PlanPosition[] } | null;
  tage: PlanTag[];
  packzettel: PlanPackzettel[];
  material: PlanMaterial[];
  /** Offene Vorschläge im ganzen Plan. */
  vorschlaege: number;
  offen: { positionen_ohne_tag: number; tage_ohne_datum: number };
  /** Etappe 7: wie viele Leistungen abgehakt sind, welche nach allen Tagen offen. */
  leistungsstand: { erledigt: number; offen: string[]; abrechnungsbereit: boolean };
  vorgabe: { beginn: string; ende: string };
};

export type EintragenBody = {
  modus: "am_stueck" | "einzeln";
  tag_ids: string[];
  start?: string;
  daten?: Record<string, string>;
  wochenende_ueberspringen: boolean;
  beginn: string;
  ende: string;
  vorschau: boolean;
};

export type EintragenAntwort = {
  eintraege: { tag_id: string; plan_nr: number | null; datum: string; tag_text: string; start_at: string; end_at: string }[];
  hinweise: string[];
  eingetragen: boolean;
  plan?: Arbeitsplan;
};

export const BEDARF_ARTEN: { art: BedarfArt; text: string; zeichen: string }[] = [
  { art: "lieferung", text: "Lieferung", zeichen: "🚚" },
  { art: "maschine", text: "Maschine", zeichen: "🚜" },
  { art: "besorgen", text: "Besorgen", zeichen: "🛒" },
  { art: "hinweis", text: "Hinweis", zeichen: "📌" },
];

export function bedarfArt(kategorie: string): { art: string; text: string; zeichen: string } {
  return BEDARF_ARTEN.find((a) => a.art === kategorie) ?? { art: kategorie, text: "Hinweis", zeichen: "📌" };
}

/** „Tag 3" — oder „Nacharbeit" für einen Nacharbeitstag ohne Nummer. */
export function tagName(t: Pick<PlanTag, "plan_nr" | "nacharbeit">): string {
  if (t.plan_nr == null) return t.nacharbeit ? "Nacharbeit" : "Tag";
  return t.nacharbeit ? `Tag ${t.plan_nr} · Nacharbeit` : `Tag ${t.plan_nr}`;
}

/** „Fr 09.10." aus "2026-10-09". */
export function tagKurzWochentag(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const wt = d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "");
  return `${wt} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
}

/** „09:00–16:00" aus zwei ISO-Zeitpunkten, Berliner Zeit. */
export function zeitspanne(start: string, ende: string): string {
  const f = (s: string) => {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
  };
  const a = f(start), b = f(ende);
  return a && b ? `${a}–${b}` : a;
}

/** Positionsnummer im Angebot (1-basiert) — „Pos. 3". */
export function positionsNummer(plan: Arbeitsplan, anker: string): number {
  const i = (plan.angebot?.positionen || []).findIndex((p) => p.id === anker);
  return i < 0 ? 0 : i + 1;
}
